import { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Sparkles, Calendar, ArrowRight } from 'lucide-react';
import { CONTACT_INFO, PROMO, isPromoActive } from '@/lib/data';
import { LUXE } from '@/constants/testIds';

const links = [
  { to: '/', label: 'Home', testId: LUXE.navHome },
  { to: '/services', label: 'Services', testId: LUXE.navServices },
  { to: '/gallery', label: 'Gallery', testId: LUXE.navGallery },
  { to: '/about', label: 'About', testId: LUXE.navAbout },
  { to: '/contact', label: 'Contact', testId: LUXE.navContact },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#0B0F1A]/85 backdrop-blur-xl border-b border-[#C9A24A]/15'
          : 'bg-transparent'
      }`}
    >
      {isPromoActive() && (
        <div
          data-testid="promo-strip-banner"
          className="bg-gradient-to-r from-[#7a1620] via-[#4a0a15] to-[#7a1620] border-b border-[#C9A24A]/40"
        >
          <div className="luxe-container py-2 flex items-center justify-center gap-3 sm:gap-5 flex-wrap text-center">
            <span className="text-[10px] uppercase tracking-[0.32em] text-[#C9A24A] font-semibold flex items-center gap-2">
              <Sparkles size={12} /> 4th of July Special
            </span>
            <span className="hidden sm:inline text-white/85 text-xs">
              Full Detail <span className="text-[#C9A24A] font-semibold">$70/$80</span> · Interior{' '}
              <span className="text-[#C9A24A] font-semibold">$40/$50</span>
            </span>
            <span className="text-white/70 text-xs flex items-center gap-1.5">
              <Calendar size={12} className="text-[#C9A24A]" /> {PROMO.validFromLabel} –{' '}
              {PROMO.validToLabel}
            </span>
            <Link
              to="/booking"
              data-testid="promo-strip-book"
              className="text-[10px] uppercase tracking-[0.28em] text-[#C9A24A] hover:text-white transition-colors flex items-center gap-1.5 font-semibold"
            >
              Book <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      )}
      <div className="luxe-container flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid={LUXE.navLogo}
          className="flex items-center gap-3 group"
        >
          <span className="flex items-center justify-center w-10 h-10 border border-[#C9A24A]/60 rotate-45 group-hover:border-[#C9A24A] transition-colors">
            <span className="-rotate-45 text-[#C9A24A] font-serif text-lg leading-none">
              L
            </span>
          </span>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif text-lg tracking-widest text-white">
              LAVISH LUXE
            </span>
            <span className="text-[10px] uppercase tracking-[0.32em] text-[#C9A24A]">
              Detailing
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={link.testId}
              end={link.to === '/'}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.28em] transition-colors relative py-2 ${
                  isActive
                    ? 'text-[#C9A24A]'
                    : 'text-white/70 hover:text-[#C9A24A]'
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {link.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-px bg-[#C9A24A] transition-all ${
                      isActive ? 'w-full' : 'w-0'
                    }`}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href={`tel:${CONTACT_INFO.phoneRaw}`}
            data-testid="nav-call-link"
            className="hidden md:flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/70 hover:text-[#C9A24A] transition-colors"
          >
            <Phone size={14} className="text-[#C9A24A]" />
            {CONTACT_INFO.phone}
          </a>
          <Link
            to="/booking"
            data-testid={LUXE.navBookCta}
            className="hidden md:inline-flex btn-book !py-3 !px-6 text-[10px]"
          >
            Book Now
          </Link>
          <button
            type="button"
            data-testid={LUXE.navMobileToggle}
            aria-label="Toggle menu"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#0B0F1A]/98 backdrop-blur-xl border-t border-[#C9A24A]/15">
          <div className="luxe-container py-8 flex flex-col gap-6">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`mobile-${link.testId}`}
                end={link.to === '/'}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.28em] ${
                    isActive ? 'text-[#C9A24A]' : 'text-white/80'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/booking"
              data-testid="mobile-nav-book-cta"
              className="btn-book self-start"
            >
              Book Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
